import React, { useEffect, useMemo, useState } from 'react';
import { api, ApiError, downloadFile } from '../shared/api';
import { formatMoney, formatNumber, formatDate } from '../shared/formatting';
import { readUrlParam, useUrlSync } from '../shared/urlState';

type Locale = 'hr' | 'en';
type Nacin = 'zbirno' | 'rasclanjeno' | 'samo';

export type GkCompany = { code: string; name: string; rows: number; date_min: string | null; date_max: string | null };
type Meta = { companies: GkCompany[]; default_code: string };

type Node = {
    konto: string; naziv: string | null; nazivi: string[]; razina: number; parent: string | null; djeca: number; u_gk: boolean;
    n: number; ps: number; d: number; p: number; saldo: number; own_n: number; own_saldo: number; izravno: boolean; zadnje: string | null;
};
type Razred = { razred: string; label: string; naziv: string; n: number; saldo: number };
type Stablo = { godina: number; do: string | null; dopune: boolean; rows: Node[]; razredi: Razred[]; totals: { ps: number; d: number; p: number; razlika: number } };

type Line = {
    id: number; konto: string; datum: string | null; temeljnica: string; ps: boolean; vrsta: string | null; dokument: string | null;
    partner: string | null; opis: string | null; d: number; p: number; iznos: number; mtp?: string | null; kumulativ?: number;
};
type Top = { naziv: string; n: number; saldo: number };
type KontoDetalj = {
    konto: string; naziv: string | null; nazivi: string[]; razred: string; razred_naziv: string | null;
    put: { konto: string; naziv: string | null }[];
    brojke: { ps: number; d: number; p: number; saldo: number; n: number; prvo: string | null; zadnje: string | null };
    izravno: { n: number; saldo: number };
    djeca: { konto: string; naziv: string | null; n: number; saldo: number; djeca: number }[];
    mjeseci: { mjesec: string; n: number; d: number; p: number }[];
    zadnja: Line[]; partneri: Top[]; mtp: Top[]; vrste: Top[];
    nacrti: { id: number; opis: string | null; status: string; radna: string; stavki: number; d: number; p: number }[];
    saldo_nakon_nacrta: number;
};
type Kartica = {
    konto: string; naziv: string | null; nacin: Nacin;
    totals: { n: number; d: number; p: number; saldo: number };
    page: number; page_size: number; pages: number; rows: Line[];
    po_kontu: { konto: string; naziv: string | null; n: number; d: number; p: number; saldo: number }[];
};

export type GkAlat = { key: string; label: string };

/** Bilancni razredi hrvatskog kontnog plana; 4 i 7 su rezultat, 8 obracun rezultata. */
const BILANCA = ['0', '1', '2', '3', '6', '9'];
const REZULTAT = ['4', '7', '8'];

const CSS = `
.gk-root{position:fixed;inset:0;z-index:10000;display:flex;flex-direction:column;background:var(--ad-bg,#fff);color:var(--ad-ink,#111);font-size:13px}
.gk-bar{height:48px;flex:0 0 48px;display:flex;align-items:center;gap:8px;padding:0 10px;border-bottom:1px solid rgba(var(--ad-soft-rgb,120,120,120),.35);overflow-x:auto}
.gk-bar>*{flex-shrink:0}
.gk-bar select,.gk-bar input{height:30px;font-size:13px;padding:2px 6px;width:auto;margin:0}
.gk-bar select.gk-firma{max-width:280px}
.gk-bar label{display:flex;align-items:center;gap:4px;margin:0;font-size:12px}
.gk-bar input[type=checkbox]{height:auto}
.gk-bar .gk-title{font-weight:600;white-space:nowrap;margin-right:6px}
.gk-chips{display:flex;gap:6px;align-items:center;padding:6px 10px;flex-wrap:wrap;border-bottom:1px solid rgba(var(--ad-soft-rgb,120,120,120),.2)}
.gk-chip{border:1px solid rgba(var(--ad-soft-rgb,120,120,120),.5);background:transparent;color:inherit;border-radius:4px;padding:3px 8px;cursor:pointer;font-size:12px;white-space:nowrap}
.gk-chip.on{background:var(--ad-ink,#111);color:var(--ad-bg,#fff);border-color:var(--ad-ink,#111)}
.gk-chip:disabled{opacity:.35;cursor:default}
.gk-chip.link{border-style:dashed}
.gk-warn{padding:6px 10px;background:#f3f3f3;border-bottom:1px solid #bbb;font-size:12px}
.gk-main{flex:1;display:flex;min-height:0}
.gk-scroll{flex:1;overflow:auto;min-width:0}
.gk-t{border-collapse:collapse;width:100%;min-width:0}
.gk-t th{position:sticky;top:0;background:var(--ad-bg,#fff);text-align:left;font-weight:600;padding:5px 8px;border-bottom:1px solid #999;white-space:nowrap;z-index:1}
.gk-t td{padding:3px 8px;border-bottom:1px solid rgba(var(--ad-soft-rgb,120,120,120),.18);white-space:nowrap}
.gk-t td.wrap{white-space:normal;min-width:180px}
.gk-t .num{text-align:right;font-variant-numeric:tabular-nums}
.gk-t tbody tr{cursor:pointer}
.gk-t tbody tr:hover{background:rgba(var(--ad-soft-rgb,120,120,120),.10)}
.gk-t tr.sel{background:rgba(var(--ad-soft-rgb,120,120,120),.22)!important}
.gk-t tr.r0 td{font-weight:700;background:rgba(var(--ad-soft-rgb,120,120,120),.08)}
.gk-t tr.r1 td{font-weight:600}
.gk-t tr.grp td{font-weight:600;background:rgba(var(--ad-soft-rgb,120,120,120),.12);cursor:default}
.gk-t tfoot td{position:sticky;bottom:0;background:var(--ad-bg,#fff);border-top:1px solid #999;font-weight:700;padding:5px 8px}
.gk-tog{display:inline-block;width:16px;text-align:center;cursor:pointer;user-select:none}
.gk-muted{opacity:.6}
.gk-t tr.gk-izr td{font-style:italic;opacity:.8}
.gk-drawer{flex:0 0 520px;max-width:100%;border-left:1px solid #999;overflow:auto;padding:12px 14px;background:var(--ad-bg,#fff)}
.gk-drawer .gk-t{table-layout:fixed;min-width:0}
.gk-drawer .gk-t td{overflow:hidden;text-overflow:ellipsis}
.gk-drawer .gk-t td.wrap{white-space:normal;min-width:0}
.gk-drawer .gk-t .num{width:132px}
.gk-drawer .gk-t td:first-child:not(.wrap),.gk-drawer .gk-t th:first-child{width:78px}
.gk-drawer .gk-t .gk-cnt{width:52px}
.gk-drawer h2{font-size:16px;margin:2px 0 4px}
.gk-drawer h3{font-size:13px;margin:14px 0 4px;border-bottom:1px solid #ccc;padding-bottom:2px}
.gk-kv{display:grid;grid-template-columns:auto 1fr;gap:2px 12px}
.gk-kv div:nth-child(odd){opacity:.7}
.gk-kv div:nth-child(even){text-align:right;font-variant-numeric:tabular-nums}
.gk-bar-g{display:inline-block;height:8px;background:#888;vertical-align:middle}
.gk-modal{position:fixed;inset:0;z-index:10050;background:rgba(0,0,0,.35);display:flex;align-items:center;justify-content:center;padding:16px}
.gk-modal>div{background:var(--ad-bg,#fff);border:1px solid #888;padding:16px;max-width:460px;width:100%}
.gk-opt{display:block;width:100%;text-align:left;margin:6px 0;padding:8px 10px;border:1px solid #999;background:transparent;color:inherit;cursor:pointer}
.gk-opt:hover{background:rgba(var(--ad-soft-rgb,120,120,120),.12)}
.gk-menu{position:relative}
.gk-menu ul{position:fixed;list-style:none;margin:0;padding:4px 0;background:var(--ad-bg,#fff);border:1px solid #999;z-index:10060;min-width:220px}
.gk-menu li{padding:6px 12px;cursor:pointer;white-space:nowrap}
.gk-menu li:hover{background:rgba(var(--ad-soft-rgb,120,120,120),.12)}
@media (max-width:760px){.gk-root .gk-hide-m{display:none!important}.gk-t td.wrap{min-width:110px}.gk-t td,.gk-t th{padding-left:5px;padding-right:5px}.gk-chips{flex-wrap:nowrap;overflow-x:auto}.gk-drawer{position:fixed;inset:48px 0 0 0;flex:none;border-left:0}}
`;

function err(e: unknown, fallback: string): string {
    return e instanceof ApiError || e instanceof Error ? e.message : fallback;
}

function qs(o: Record<string, string | number | boolean | null | undefined>): string {
    const s = new URLSearchParams();
    Object.entries(o).forEach(([k, v]) => {
        if (v === null || v === undefined || v === '' || v === false) return;
        s.set(k, v === true ? '1' : String(v));
    });
    return s.toString();
}

/**
 * Drawer s detaljem konta: put u hijerarhiji, brojke, sastav salda po analitici,
 * promet po mjesecima, partneri, MTP, vrste dokumenata, zadnja knjizenja i nacrti.
 * Ne otvara karticu sam; to radi gumb Otvori.
 */
function KontoDrawer({ locale, token, period, konto, onClose, onSelect, onOpen }: {
    locale: Locale; token: string | null; period: string; konto: string;
    onClose: () => void; onSelect: (k: string) => void; onOpen: (d: KontoDetalj) => void;
}) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const m = (v: number) => formatMoney(v, locale);
    const [d, setD] = useState<KontoDetalj | null>(null);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        setD(null);
        setError(null);
        let live = true;
        api<KontoDetalj>(`/gl-analitika/konto/${konto}?${period}`, token)
            .then((r) => { if (live) setD(r); })
            .catch((e) => { if (live) setError(err(e, t('Učitavanje nije uspjelo.', 'Loading failed.'))); });
        return () => { live = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [konto, period, token]);

    const maxMj = d ? Math.max(1, ...d.mjeseci.map((x) => Math.max(x.d, x.p))) : 1;
    const topTable = (title: string, list: Top[]) => list.length === 0 ? null : (
        <>
            <h3>{title}</h3>
            <table className="gk-t"><tbody>
                {list.map((x) => (
                    <tr key={x.naziv} style={{ cursor: 'default' }}>
                        <td className="wrap">{x.naziv}</td>
                        <td className="num gk-muted gk-cnt">{formatNumber(x.n, locale)}</td>
                        <td className="num">{m(x.saldo)}</td>
                    </tr>
                ))}
            </tbody></table>
        </>
    );

    return (
        <aside className="gk-drawer" aria-label={t('Detalj konta', 'Account detail')}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="gk-muted" style={{ fontSize: 12 }}>
                        {d?.put.map((p, i) => (
                            <span key={p.konto}>
                                {i > 0 ? ' / ' : ''}
                                <a href="#" onClick={(e) => { e.preventDefault(); onSelect(p.konto); }} title={p.naziv ?? ''}>{p.konto}</a>
                            </span>
                        ))}
                    </div>
                    <h2>{konto} {d?.naziv ?? ''}</h2>
                    {d && d.nazivi.length > 0 ? <div className="gk-muted" style={{ fontSize: 12 }}>{t('Drugi nazivi u GK', 'Other names in GL')}: {d.nazivi.join('; ')}</div> : null}
                </div>
                <button type="button" className="secondary compact" onClick={onClose} aria-label={t('Zatvori', 'Close')}>✕</button>
            </div>

            {error ? <p style={{ color: '#b91c1c' }}>{error}</p> : null}
            {!d && !error ? <p className="gk-muted">{t('Učitavam…', 'Loading…')}</p> : null}

            {d ? (
                <>
                    <div style={{ display: 'flex', gap: 8, margin: '10px 0' }}>
                        <button type="button" className="compact" onClick={() => onOpen(d)}>{t('Otvori karticu', 'Open ledger card')}</button>
                    </div>

                    <div className="gk-kv">
                        <div>{t('Razred', 'Class')}</div><div>{d.razred} {d.razred_naziv ?? ''}</div>
                        <div>{t('Početno stanje', 'Opening balance')}</div><div>{m(d.brojke.ps)}</div>
                        <div>{t('Promet duguje', 'Debit turnover')}</div><div>{m(d.brojke.d)}</div>
                        <div>{t('Promet potražuje', 'Credit turnover')}</div><div>{m(d.brojke.p)}</div>
                        <div><strong>{t('Saldo', 'Balance')}</strong></div><div><strong>{m(d.brojke.saldo)}</strong></div>
                        <div>{t('Stavki', 'Postings')}</div><div>{formatNumber(d.brojke.n, locale)}</div>
                        <div>{t('Prvo / zadnje knjiženje', 'First / last posting')}</div>
                        <div>{d.brojke.prvo ? formatDate(d.brojke.prvo, locale) : '-'} / {d.brojke.zadnje ? formatDate(d.brojke.zadnje, locale) : '-'}</div>
                        {d.nacrti.length > 0 ? (<><div>{t('Saldo nakon nacrta', 'Balance after drafts')}</div><div>{m(d.saldo_nakon_nacrta)}</div></>) : null}
                    </div>

                    {d.djeca.length > 0 ? (
                        <>
                            <h3>{t('Saldo dolazi s konta', 'Balance comes from')} ({d.djeca.length})</h3>
                            {d.izravno.n > 0 ? (
                                <p className="gk-muted" style={{ margin: '0 0 4px' }}>
                                    {t('Izravno na ovom kontu', 'Posted directly on this account')}: {formatNumber(d.izravno.n, locale)} {t('stavki', 'postings')}, {m(d.izravno.saldo)}
                                </p>
                            ) : null}
                            <table className="gk-t"><tbody>
                                {d.djeca.map((c) => (
                                    <tr key={c.konto} onClick={() => onSelect(c.konto)} title={t('Prikaži detalj', 'Show detail')}>
                                        <td>{c.konto}</td>
                                        <td className="wrap">{c.naziv ?? ''}{c.djeca > 0 ? <span className="gk-muted"> ({c.djeca})</span> : null}</td>
                                        <td className="num">{m(c.saldo)}</td>
                                    </tr>
                                ))}
                            </tbody></table>
                        </>
                    ) : null}

                    {d.mjeseci.length > 0 ? (
                        <>
                            <h3>{t('Promet po mjesecima (bez PS)', 'Monthly turnover (excl. opening)')}</h3>
                            <table className="gk-t"><thead><tr>
                                <th>{t('Mjesec', 'Month')}</th><th className="num">{t('Duguje', 'Debit')}</th><th className="num">{t('Potražuje', 'Credit')}</th><th />
                            </tr></thead><tbody>
                                {d.mjeseci.map((x) => (
                                    <tr key={x.mjesec} style={{ cursor: 'default' }}>
                                        <td>{x.mjesec}</td><td className="num">{m(x.d)}</td><td className="num">{m(x.p)}</td>
                                        <td style={{ width: 90 }}>
                                            <span className="gk-bar-g" style={{ width: Math.round(40 * x.d / maxMj) }} />
                                            <span className="gk-bar-g" style={{ width: Math.round(40 * x.p / maxMj), background: '#ccc', marginLeft: 2 }} />
                                        </td>
                                    </tr>
                                ))}
                            </tbody></table>
                        </>
                    ) : null}

                    {topTable(t('Najčešći partneri', 'Top partners'), d.partneri)}
                    {topTable(t('Mjesta troška', 'Cost centres'), d.mtp)}
                    {topTable(t('Vrste dokumenata', 'Document types'), d.vrste)}

                    {d.zadnja.length > 0 ? (
                        <>
                            <h3>{t('Zadnja knjiženja', 'Latest postings')}</h3>
                            <table className="gk-t"><tbody>
                                {d.zadnja.map((r) => (
                                    <tr key={r.id} style={{ cursor: 'default' }}>
                                        <td>{r.datum ? formatDate(r.datum, locale) : ''}</td>
                                        <td className="wrap">{r.konto !== konto ? <span className="gk-muted">{r.konto} </span> : null}{r.partner ?? r.opis ?? r.dokument ?? ''}</td>
                                        <td className="num">{m(r.iznos)}</td>
                                    </tr>
                                ))}
                            </tbody></table>
                        </>
                    ) : null}

                    {d.nacrti.length > 0 ? (
                        <>
                            <h3>{t('Otvoreni nacrti na kontu', 'Open drafts on account')}</h3>
                            <table className="gk-t"><tbody>
                                {d.nacrti.map((x) => (
                                    <tr key={x.id} style={{ cursor: 'default' }}>
                                        <td>#{x.id}</td><td className="wrap">{x.opis ?? ''} <span className="gk-muted">({x.status}, {x.radna})</span></td>
                                        <td className="num">{m(x.d - x.p)}</td>
                                    </tr>
                                ))}
                            </tbody></table>
                        </>
                    ) : null}
                </>
            ) : null}
        </aside>
    );
}

/** Pitanje prije otvaranja konta koji ima analitiku. */
function NacinDialog({ locale, d, onPick, onClose }: { locale: Locale; d: KontoDetalj; onPick: (n: Nacin) => void; onClose: () => void }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    return (
        <div className="gk-modal" onClick={onClose}>
            <div onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
                <strong>{d.konto} {d.naziv ?? ''}</strong>
                <p style={{ margin: '6px 0 10px' }}>
                    {t(`Konto ima ${d.djeca.length} analitičkih konta. Kako otvoriti karticu?`, `Account has ${d.djeca.length} sub-accounts. How to open the card?`)}
                </p>
                <button type="button" className="gk-opt" onClick={() => onPick('zbirno')} autoFocus>
                    <strong>{t('Sintetika', 'Synthetic')}</strong><br />
                    <span className="gk-muted">{t('Sva knjiženja konta i analitike zajedno, kronološki, jedan saldo.', 'All postings of the account and sub-accounts together, chronological, one balance.')}</span>
                </button>
                <button type="button" className="gk-opt" onClick={() => onPick('rasclanjeno')}>
                    <strong>{t('Analitika', 'Analytic')}</strong><br />
                    <span className="gk-muted">{t('Po analitičkom kontu: zbroj svakog konta, zatim njegova knjiženja i saldo.', 'Per sub-account: each account total, then its postings and balance.')}</span>
                </button>
                {d.izravno.n > 0 ? (
                    <button type="button" className="gk-opt" onClick={() => onPick('samo')}>
                        <strong>{t('Samo ovaj konto', 'This account only')}</strong><br />
                        <span className="gk-muted">{t(`Samo ${d.izravno.n} stavki knjiženih izravno na ${d.konto}.`, `Only ${d.izravno.n} postings made directly on ${d.konto}.`)}</span>
                    </button>
                ) : null}
                <div style={{ textAlign: 'right', marginTop: 8 }}>
                    <button type="button" className="secondary compact" onClick={onClose}>{t('Odustani', 'Cancel')}</button>
                </div>
            </div>
        </div>
    );
}

/**
 * Kartica konta s kumulativnim saldom. Saldo tece od pocetka godine i preko
 * stranica (racuna ga server). U nacinu rasclanjeno saldo krece od nule na
 * svakom analitickom kontu.
 */
function KarticaView({ locale, token, period, exportBase, konto, nacin, onNacin, onBack }: {
    locale: Locale; token: string | null; period: string; exportBase: Record<string, string | boolean>;
    konto: string; nacin: Nacin; onNacin: (n: Nacin) => void; onBack: () => void;
}) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const m = (v: number) => formatMoney(v, locale);
    const [q, setQ] = useState('');
    const [qApplied, setQApplied] = useState('');
    const [page, setPage] = useState(1);
    const [data, setData] = useState<Kartica | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [exporting, setExporting] = useState(false);

    useEffect(() => { setPage(1); }, [konto, nacin, qApplied, period]);

    useEffect(() => {
        let live = true;
        setLoading(true);
        setError(null);
        api<Kartica>(`/gl-analitika/kartica?${period}&${qs({ konto, nacin, q: qApplied, page })}`, token)
            .then((r) => { if (live) setData(r); })
            .catch((e) => { if (live) setError(err(e, t('Učitavanje nije uspjelo.', 'Loading failed.'))); })
            .finally(() => { if (live) setLoading(false); });
        return () => { live = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [konto, nacin, qApplied, page, period, token]);

    async function exportXlsx() {
        setExporting(true);
        try {
            await downloadFile(`/gl-analitika/export.xlsx?${qs({ ...exportBase, konto_od: konto, konto_do: konto, q: qApplied })}`, token,
                `kartica-${konto}-${String(exportBase.from).slice(0, 4)}.xlsx`);
        } catch (e) {
            setError(err(e, t('Izvoz nije uspio.', 'Export failed.')));
        } finally {
            setExporting(false);
        }
    }

    const showKonto = nacin !== 'samo';
    const naziviPo = new Map((data?.po_kontu ?? []).map((x) => [x.konto, x]));
    const body: React.ReactNode[] = [];
    let cur: string | null = null;
    (data?.rows ?? []).forEach((r) => {
        if (nacin === 'rasclanjeno' && r.konto !== cur) {
            cur = r.konto;
            const g = naziviPo.get(r.konto);
            body.push(
                <tr key={`g${r.konto}-${r.id}`} className="grp">
                    <td colSpan={showKonto ? 8 : 7}>{r.konto} {g?.naziv ?? ''} <span className="gk-muted">· {formatNumber(g?.n ?? 0, locale)} {t('stavki', 'postings')}</span></td>
                    <td className="num gk-hide-m">{g ? m(g.d) : ''}</td><td className="num gk-hide-m">{g ? m(g.p) : ''}</td><td className="num">{g ? m(g.saldo) : ''}</td>
                </tr>,
            );
        }
        body.push(
            <tr key={r.id} style={{ cursor: 'default' }}>
                <td>{r.datum ? formatDate(r.datum, locale) : ''}</td>
                <td>{r.ps ? <strong title={t('Početno stanje', 'Opening balance')}>PS</strong> : r.temeljnica}</td>
                {showKonto ? <td>{r.konto}</td> : null}
                <td className="gk-hide-m">{r.vrsta ?? ''}</td>
                <td className="gk-hide-m">{r.dokument ?? ''}</td>
                <td className="wrap">{r.partner ?? ''}</td>
                <td className="gk-hide-m">{r.mtp ?? ''}</td>
                <td className="wrap gk-hide-m">{r.opis ?? ''}</td>
                <td className="num gk-hide-m">{r.d ? m(r.d) : ''}</td>
                <td className="num gk-hide-m">{r.p ? m(r.p) : ''}</td>
                <td className="num">{m(r.kumulativ ?? 0)}</td>
            </tr>,
        );
    });

    const nacini: [Nacin, string][] = [['zbirno', t('Sintetika', 'Synthetic')], ['rasclanjeno', t('Analitika', 'Analytic')], ['samo', t('Samo konto', 'Account only')]];

    return (
        <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 }}>
            <div className="gk-chips">
                <button type="button" className="gk-chip link" onClick={onBack}>← {t('Stablo konta', 'Account tree')}</button>
                <strong style={{ marginRight: 8 }}>{t('Kartica', 'Card')} {konto} {data?.naziv ?? ''}</strong>
                {nacini.map(([k, l]) => <button key={k} type="button" className={`gk-chip${nacin === k ? ' on' : ''}`} onClick={() => onNacin(k)}>{l}</button>)}
                <form onSubmit={(e) => { e.preventDefault(); setQApplied(q.trim()); }} style={{ display: 'flex', gap: 4 }}>
                    <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('partner, opis, dokument', 'partner, text, document')} style={{ height: 26, width: 200 }} />
                </form>
                <span style={{ flex: 1 }} />
                {data ? (
                    <span className="gk-muted">
                        {formatNumber(data.totals.n, locale)} {t('stavki', 'postings')} · {t('str.', 'p.')} {data.page}/{data.pages}
                    </span>
                ) : null}
                <button type="button" className="gk-chip" disabled={!data || page <= 1 || loading} onClick={() => setPage((p) => p - 1)}>‹</button>
                <button type="button" className="gk-chip" disabled={!data || page >= data.pages || loading} onClick={() => setPage((p) => p + 1)}>›</button>
                <button type="button" className="gk-chip" disabled={exporting || !data || data.totals.n === 0} onClick={exportXlsx}
                    title={nacin === 'samo' ? t('XLS uključuje i analitiku konta.', 'XLS includes sub-accounts.') : ''}>
                    {exporting ? t('Izvozim…', 'Exporting…') : 'XLS'}
                </button>
            </div>
            {error ? <div className="gk-warn" style={{ color: '#b91c1c' }}>{error}</div> : null}
            <div className="gk-scroll" style={{ opacity: loading ? 0.6 : 1 }}>
                <table className="gk-t">
                    <thead><tr>
                        <th>{t('Datum', 'Date')}</th><th>{t('Temeljnica', 'Journal')}</th>{showKonto ? <th>{t('Konto', 'Account')}</th> : null}
                        <th className="gk-hide-m">{t('Vrsta', 'Type')}</th><th className="gk-hide-m">{t('Dokument', 'Document')}</th>
                        <th>{t('Partner', 'Partner')}</th><th className="gk-hide-m">MTP</th><th className="gk-hide-m">{t('Opis', 'Description')}</th>
                        <th className="num gk-hide-m">{t('Duguje', 'Debit')}</th><th className="num gk-hide-m">{t('Potražuje', 'Credit')}</th><th className="num">{t('Saldo', 'Balance')}</th>
                    </tr></thead>
                    <tbody>{body}</tbody>
                    {data ? (
                        <tfoot><tr>
                            <td colSpan={showKonto ? 8 : 7}>{t('Ukupno', 'Total')}</td>
                            <td className="num gk-hide-m">{m(data.totals.d)}</td><td className="num gk-hide-m">{m(data.totals.p)}</td><td className="num">{m(data.totals.saldo)}</td>
                        </tr></tfoot>
                    ) : null}
                </table>
                {data && data.rows.length === 0 ? <p className="gk-muted" style={{ padding: 10 }}>{t('Nema knjiženja.', 'No postings.')}</p> : null}
            </div>
        </div>
    );
}

/**
 * Modul 10.1, Glavna knjiga po kontu, preko cijelog zaslona. Stablo razred,
 * skupina, sintetika, analitika; klik na redak otvara drawer s detaljem, gumb
 * Otvori karticu (uz pitanje sintetika ili analitika kad konto ima djecu).
 * Stari pregledi (filtri, temeljnice, bilanca, promjene, uparivanje) su u
 * izborniku Alati i crta ih ljuska kroz renderAlat. Stanje je u URL-u (gk*).
 */
export function GlGlavnaKnjiga({ locale, token, onBack, alati, renderAlat }: {
    locale: Locale; token: string | null; onBack?: () => void;
    alati: GkAlat[]; renderAlat: (key: string, company: string, godina: number, goTo: (key: string) => void) => React.ReactNode;
}) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const m = (v: number) => formatMoney(v, locale);

    const [meta, setMeta] = useState<Meta | null>(null);
    const [company, setCompany] = useState(readUrlParam('gkf', ''));
    const [godina, setGodina] = useState(Number(readUrlParam('gky', '0')) || 0);
    const [doDatum, setDoDatum] = useState(readUrlParam('gkdo', ''));
    const [dopune, setDopune] = useState(readUrlParam('gkdp', '') === '1');
    const [razredi, setRazredi] = useState<string>(readUrlParam('gkr', ''));
    const [q, setQ] = useState(readUrlParam('gkq', ''));
    const [sel, setSel] = useState(readUrlParam('gkk', ''));
    const [karticaKonto, setKarticaKonto] = useState(readUrlParam('gkv', ''));
    const [nacin, setNacin] = useState<Nacin>((['zbirno', 'rasclanjeno', 'samo'] as Nacin[]).find((x) => x === readUrlParam('gkn', '')) ?? 'zbirno');
    const [alat, setAlat] = useState(readUrlParam('gkt', ''));
    // fixed + koordinate gumba: .gk-bar ima overflow-x:auto i odreze absolute izbornik
    const [menu, setMenu] = useState<{ top: number; right: number } | null>(null);
    const [pitaj, setPitaj] = useState<KontoDetalj | null>(null);
    const [data, setData] = useState<Stablo | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [expanded, setExpanded] = useState<Set<string> | null>(null);

    useUrlSync('gkf', company);
    useUrlSync('gky', godina ? String(godina) : '');
    useUrlSync('gkdo', doDatum);
    useUrlSync('gkdp', dopune ? '1' : '');
    useUrlSync('gkr', razredi);
    useUrlSync('gkq', q);
    useUrlSync('gkk', sel);
    useUrlSync('gkv', karticaKonto);
    useUrlSync('gkn', karticaKonto ? nacin : '', nacin === 'zbirno');
    useUrlSync('gkt', alat);

    useEffect(() => {
        // Na mobitelu siroki sadrzaj ljuske ispod siri layout viewport; html mora rezati.
        const els = [document.documentElement, document.body];
        const prev = els.map((e) => e.style.overflow);
        els.forEach((e) => { e.style.overflow = 'hidden'; });
        return () => { els.forEach((e, i) => { e.style.overflow = prev[i]; }); };
    }, []);

    useEffect(() => {
        api<Meta>('/gl-analitika/meta', token).then((r) => {
            setMeta(r);
            const code = r.companies.some((c) => c.code === company) ? company
                : r.companies.some((c) => c.code === r.default_code) ? r.default_code : (r.companies[0]?.code ?? r.default_code);
            setCompany(code);
            const c = r.companies.find((x) => x.code === code);
            setGodina((g) => g || Number((c?.date_max ?? String(new Date().getFullYear())).slice(0, 4)));
        }).catch((e) => setError(err(e, t('Učitavanje nije uspjelo.', 'Loading failed.'))));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    const period = qs({ company, godina, do: doDatum, dopune });

    useEffect(() => {
        if (!company || !godina) return;
        let live = true;
        setLoading(true);
        setError(null);
        api<Stablo>(`/gl-analitika/stablo?${period}`, token)
            .then((r) => {
                if (!live) return;
                setData(r);
                setExpanded((e) => e ?? new Set(r.rows.filter((x) => x.razina <= 1).map((x) => x.konto)));
            })
            .catch((e) => { if (live) setError(err(e, t('Učitavanje nije uspjelo.', 'Loading failed.'))); })
            .finally(() => { if (live) setLoading(false); });
        return () => { live = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [period, token]);

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key !== 'Escape') return;
            if (menu) setMenu(null);
            else if (pitaj) setPitaj(null);
            else if (sel) setSel('');
            else if (karticaKonto) setKarticaKonto('');
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [menu, pitaj, sel, karticaKonto]);

    const aktivni = useMemo(() => {
        const sRaz = new Set(razredi ? razredi.split('') : (data?.razredi ?? []).filter((r) => r.n > 0).map((r) => r.razred));
        return sRaz;
    }, [razredi, data]);

    const visible = useMemo(() => {
        if (!data || !expanded) return [] as Node[];
        const rows = data.rows.filter((r) => aktivni.has(r.konto[0]));
        const needle = q.trim().toLowerCase();
        if (needle) {
            const by = new Map(data.rows.filter((r) => !r.izravno).map((r) => [r.konto, r]));
            const keep = new Set<string>();
            rows.forEach((r) => {
                const hit = /^\d+$/.test(needle) ? r.konto.startsWith(needle) : [r.naziv ?? '', ...r.nazivi].some((n) => n.toLowerCase().includes(needle));
                if (!hit) return;
                for (let k: string | null = r.konto; k !== null && !keep.has(k); k = by.get(k)?.parent ?? null) keep.add(k);
            });
            return rows.filter((r) => keep.has(r.konto));
        }
        const shown = new Set<string>();
        return rows.filter((r) => {
            const ok = r.parent === null || (shown.has(r.parent) && expanded.has(r.parent));
            if (ok) shown.add(r.konto);
            return ok;
        });
    }, [data, expanded, aktivni, q]);

    const ukupno = useMemo(() => {
        const s = { ps: 0, d: 0, p: 0, saldo: 0, n: 0 };
        (data?.rows ?? []).filter((r) => r.parent === null && aktivni.has(r.konto)).forEach((r) => {
            s.ps += r.ps; s.d += r.d; s.p += r.p; s.saldo += r.saldo; s.n += r.n;
        });
        return s;
    }, [data, aktivni]);

    const toggle = (k: string) => setExpanded((e) => {
        const n = new Set(e ?? []);
        if (n.has(k)) n.delete(k); else n.add(k);
        return n;
    });
    const setRaz = (list: string[]) => setRazredi(list.slice().sort().join(''));
    const toggleRaz = (r: string) => {
        const s = new Set(aktivni);
        if (s.has(r)) s.delete(r); else s.add(r);
        setRaz([...s]);
    };
    const imaju = (data?.razredi ?? []).filter((r) => r.n > 0).map((r) => r.razred);
    const isSet = (list: string[]) => {
        const want = list.filter((x) => imaju.includes(x));
        return want.length === aktivni.size && want.every((x) => aktivni.has(x));
    };

    const otvori = (d: KontoDetalj) => {
        if (d.djeca.length > 0) { setPitaj(d); return; }
        setNacin('zbirno');
        setKarticaKonto(d.konto);
        setSel('');
    };
    const pick = (n: Nacin) => {
        if (!pitaj) return;
        setNacin(n);
        setKarticaKonto(pitaj.konto);
        setPitaj(null);
        setSel('');
    };

    const c = meta?.companies.find((x) => x.code === company);
    const years: number[] = [];
    for (let y = Number((c?.date_max ?? `${godina}`).slice(0, 4)); y >= Number((c?.date_min ?? `${godina}`).slice(0, 4)); y--) years.push(y);
    if (godina && !years.includes(godina)) years.unshift(godina);
    const exportBase = { company, datum_po: 'razdoblje', from: `${godina}-01-01`, to: doDatum || `${godina}-12-31`, dopune };
    const alatLabel = alati.find((a) => a.key === alat)?.label;

    return (
        <div className="gk-root">
            <style>{CSS}</style>
            <div className="gk-bar">
                {onBack ? <button type="button" className="secondary compact" onClick={onBack} title={t('Natrag na nadzornu ploču', 'Back to dashboard')}>←</button> : null}
                <span className="gk-title">10.1 {t('Glavna knjiga', 'General ledger')}{alatLabel ? ` / ${alatLabel}` : ''}</span>
                <select className="gk-firma" value={company} onChange={(e) => { setCompany(e.target.value); setSel(''); setKarticaKonto(''); setExpanded(null); }} title={t('Firma', 'Company')}>
                    {(meta?.companies ?? []).map((x) => <option key={x.code} value={x.code}>{x.code} · {x.name}</option>)}
                </select>
                <select value={godina || ''} onChange={(e) => { setGodina(Number(e.target.value)); setDoDatum(''); setExpanded(null); }} title={t('Godina', 'Year')}>
                    {years.map((y) => <option key={y} value={y}>{y}</option>)}
                </select>
                {!alat ? (
                    <>
                        <label className="gk-hide-m" style={{ whiteSpace: 'nowrap' }} title={t('Stanje na dan; prazno = cijela godina', 'Balance as of; empty = whole year')}>
                            {t('stanje do', 'as of')} <input type="date" value={doDatum} min={`${godina}-01-01`} max={`${godina}-12-31`} onChange={(e) => setDoDatum(e.target.value)} />
                        </label>
                        <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('konto ili naziv', 'account or name')} style={{ width: 170 }} />
                        <label className="gk-hide-m" style={{ whiteSpace: 'nowrap' }} title={t('Reci koje je BIS dodao (imputed, RDG dopuna); nisu u Farosu.', 'Rows added by BIS; not in Faros.')}>
                            <input type="checkbox" checked={dopune} onChange={(e) => setDopune(e.target.checked)} /> {t('BIS dopune', 'BIS top-ups')}
                        </label>
                    </>
                ) : null}
                <span style={{ flex: 1 }} />
                {loading ? <span className="gk-muted">{t('Učitavam…', 'Loading…')}</span> : null}
                <div className="gk-menu">
                    <button type="button" className="secondary compact" onClick={(e) => { const r = e.currentTarget.getBoundingClientRect(); setMenu((x) => (x ? null : { top: r.bottom + 2, right: window.innerWidth - r.right })); }} aria-expanded={!!menu}>{t('Alati', 'Tools')} ▾</button>
                    {menu ? (
                        <ul style={{ top: menu.top, right: menu.right }} onMouseLeave={() => setMenu(null)}>
                            <li onClick={() => { setAlat(''); setMenu(null); }}><strong>{t('Glavna knjiga po kontu', 'Ledger by account')}</strong></li>
                            {alati.map((a) => <li key={a.key} onClick={() => { setAlat(a.key); setSel(''); setMenu(null); }}>{a.label}</li>)}
                        </ul>
                    ) : null}
                </div>
            </div>

            {alat && company && godina ? (
                <div className="gk-scroll" style={{ padding: 12 }}>{renderAlat(alat, company, godina, setAlat)}</div>
            ) : (
                <>
                    {!karticaKonto ? (
                        <div className="gk-chips">
                            <span className="gk-muted">{t('Razredi', 'Classes')}:</span>
                            {(data?.razredi ?? []).map((r) => (
                                <button key={r.razred} type="button" className={`gk-chip${aktivni.has(r.razred) ? ' on' : ''}`} disabled={r.n === 0}
                                    onClick={() => toggleRaz(r.razred)} title={`${r.naziv} · ${t('saldo', 'balance')} ${m(r.saldo)}`}>
                                    {r.razred} {r.label}
                                </button>
                            ))}
                            <span style={{ width: 8 }} />
                            <button type="button" className={`gk-chip link${isSet(BILANCA) ? ' on' : ''}`} onClick={() => setRaz(BILANCA.filter((x) => imaju.includes(x)))}>{t('Bilanca', 'Balance sheet')}</button>
                            <button type="button" className={`gk-chip link${isSet(REZULTAT) ? ' on' : ''}`} onClick={() => setRaz(REZULTAT.filter((x) => imaju.includes(x)))}>{t('Rezultat', 'P&L')}</button>
                            <button type="button" className={`gk-chip link${!razredi ? ' on' : ''}`} onClick={() => setRazredi('')}>{t('Sve', 'All')}</button>
                            <span style={{ flex: 1 }} />
                            <button type="button" className="gk-chip" onClick={() => setExpanded(new Set((data?.rows ?? []).filter((r) => r.djeca > 0).map((r) => r.konto)))}>{t('Razvij sve', 'Expand all')}</button>
                            <button type="button" className="gk-chip" onClick={() => setExpanded(new Set((data?.rows ?? []).filter((r) => r.razina <= 1).map((r) => r.konto)))}>{t('Do sintetike', 'To synthetic')}</button>
                        </div>
                    ) : null}
                    {data && Math.abs(data.totals.razlika) >= 0.01 && !karticaKonto ? (
                        <div className="gk-warn">
                            {t('Duguje i potražuje svih konta nisu u ravnoteži: razlika', 'Debits and credits of all accounts do not balance: difference')} <strong>{m(data.totals.razlika)}</strong>.
                            {' '}{t('Uvoz za ovu godinu je vjerojatno nepotpun ili su uključene BIS dopune.', 'The import for this year is probably incomplete or BIS top-ups are included.')}
                        </div>
                    ) : null}
                    {error ? <div className="gk-warn" style={{ color: '#b91c1c' }}>{error}</div> : null}

                    <div className="gk-main">
                        {karticaKonto ? (
                            <KarticaView locale={locale} token={token} period={period} exportBase={exportBase}
                                konto={karticaKonto} nacin={nacin} onNacin={setNacin} onBack={() => setKarticaKonto('')} />
                        ) : (
                            <div className="gk-scroll" style={{ opacity: loading ? 0.6 : 1 }}>
                                <table className="gk-t">
                                    <thead><tr>
                                        <th>{t('Konto', 'Account')}</th><th>{t('Naziv', 'Name')}</th>
                                        <th className="num gk-hide-m">{t('Početno stanje', 'Opening')}</th>
                                        <th className="num gk-hide-m">{t('Duguje', 'Debit')}</th>
                                        <th className="num gk-hide-m">{t('Potražuje', 'Credit')}</th>
                                        <th className="num">{t('Saldo', 'Balance')}</th>
                                        <th className="num gk-hide-m">{t('Stavki', 'Postings')}</th>
                                        <th className="gk-hide-m">{t('Zadnje', 'Last')}</th>
                                    </tr></thead>
                                    <tbody>
                                        {visible.map((r) => {
                                            const open = q.trim() !== '' || (expanded?.has(r.konto) ?? false);
                                            if (r.izravno) {
                                                return (
                                                    <tr key={`${r.konto}#izravno`} className={`gk-izr${sel === r.konto ? ' sel' : ''}`}
                                                        title={t('Knjiženja izravno na ovom kontu, bez analitike. Ovaj saldo odgovara Farosu za konto.', 'Postings directly on this account, without sub-accounts. This balance matches Faros for the account.')}
                                                        onClick={() => setSel(r.konto)} onDoubleClick={() => { setNacin('samo'); setKarticaKonto(r.konto); setSel(''); }}>
                                                        <td style={{ paddingLeft: 8 + r.razina * 16 }}><span className="gk-tog" />{r.konto}</td>
                                                        <td className="wrap">{t('izravno na kontu (saldo kao u Farosu)', 'posted directly (balance as in Faros)')}</td>
                                                        <td className="num gk-hide-m">{r.ps ? m(r.ps) : ''}</td>
                                                        <td className="num gk-hide-m">{r.d ? m(r.d) : ''}</td>
                                                        <td className="num gk-hide-m">{r.p ? m(r.p) : ''}</td>
                                                        <td className="num">{m(r.saldo)}</td>
                                                        <td className="num gk-hide-m gk-muted">{formatNumber(r.n, locale)}</td>
                                                        <td className="gk-hide-m gk-muted">{r.zadnje ? formatDate(r.zadnje, locale) : ''}</td>
                                                    </tr>
                                                );
                                            }
                                            return (
                                                <tr key={r.konto} title={r.own_n > 0 && r.djeca > 0 ? t('Zbroj konta i analitike. Saldo samo ovog konta je u retku "izravno na kontu".', 'Account plus sub-accounts. The balance of this account alone is in the "posted directly" row.') : undefined} className={`${r.razina <= 1 ? `r${r.razina}` : ''}${sel === r.konto ? ' sel' : ''}`}
                                                    onClick={() => setSel(r.konto)} onDoubleClick={() => r.djeca > 0 && toggle(r.konto)}>
                                                    <td style={{ paddingLeft: 8 + r.razina * 16 }}>
                                                        <span className="gk-tog" onClick={(e) => { e.stopPropagation(); if (r.djeca > 0) toggle(r.konto); }}>
                                                            {r.djeca > 0 ? (open ? '▾' : '▸') : ''}
                                                        </span>
                                                        {r.konto}
                                                    </td>
                                                    <td className="wrap">{r.naziv ?? ''}</td>
                                                    <td className="num gk-hide-m">{r.ps ? m(r.ps) : ''}</td>
                                                    <td className="num gk-hide-m">{r.d ? m(r.d) : ''}</td>
                                                    <td className="num gk-hide-m">{r.p ? m(r.p) : ''}</td>
                                                    <td className="num">{m(r.saldo)}</td>
                                                    <td className="num gk-hide-m gk-muted">{formatNumber(r.n, locale)}</td>
                                                    <td className="gk-hide-m gk-muted">{r.zadnje ? formatDate(r.zadnje, locale) : ''}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                    {data ? (
                                        <tfoot><tr>
                                            <td colSpan={2}>{t('Ukupno odabrani razredi', 'Total selected classes')}</td>
                                            <td className="num gk-hide-m">{m(ukupno.ps)}</td><td className="num gk-hide-m">{m(ukupno.d)}</td>
                                            <td className="num gk-hide-m">{m(ukupno.p)}</td><td className="num">{m(ukupno.saldo)}</td>
                                            <td className="num gk-hide-m">{formatNumber(ukupno.n, locale)}</td><td className="gk-hide-m" />
                                        </tr></tfoot>
                                    ) : null}
                                </table>
                                {data && visible.length === 0 ? <p className="gk-muted" style={{ padding: 10 }}>{t('Nema konta za odabir.', 'No accounts for the selection.')}</p> : null}
                            </div>
                        )}
                        {sel && !karticaKonto ? (
                            <KontoDrawer locale={locale} token={token} period={period} konto={sel}
                                onClose={() => setSel('')} onSelect={setSel} onOpen={otvori} />
                        ) : null}
                    </div>
                </>
            )}
            {pitaj ? <NacinDialog locale={locale} d={pitaj} onPick={pick} onClose={() => setPitaj(null)} /> : null}
        </div>
    );
}
